import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useMemo } from "react";
import { CatalogItemObject, Merchandise, MerchandiseCatalogItem } from "types";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { Currency } from "@/components/Common/Currency";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import {
    getMerchandiseVariantMaxSelectableQuantity,
    getMerchandiseVariantPrice,
    getSortedPurchasableMerchandiseCatalogItems,
    getVariantMinQuantity,
    isInventoryTracked,
    isMerchandiseVariantAvailable,
    isVariantOutOfStock,
    isVariantPurchasable,
} from "@/models/Merchandise";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export const CreatePurchaseMerchandiseVariantPicker = React.memo(() => {
    const merchandise = useCreatePurchaseStore((state) => state.getActiveProduct<Merchandise>());
    const hasTrackedInventory = isInventoryTracked(merchandise);

    const { data: catalogItems = [] } = useInfiniteCollection<MerchandiseCatalogItem>(
        "catalogItems",
        {
            product: merchandise.id,
            object: CatalogItemObject.WARE_CATALOG_ITEM,
        },
        {
            enabled: !!merchandise?.id,
            staleTime: 0,
        },
    );

    const sortedCatalogItems = useMemo(() => getSortedPurchasableMerchandiseCatalogItems(catalogItems), [catalogItems]);

    const totalQuantity = useCreatePurchaseStore((state) => state.getActiveItem()?.quantity ?? 0);

    // TODO XS-1401: update to give error if Quantity exceeds available stock
    const max = Number.MAX_SAFE_INTEGER;
    const error = totalQuantity > max ? `Quantity cannot exceed ${max}` : undefined;

    return (
        <div className="space-y-2">
            <div className={clsx("text-sm font-bold", { "text-danger": error })}>{error ?? "Quantity"}</div>
            <div className="flex flex-col items-start space-y-2">
                {sortedCatalogItems.map((catalogItem) => {
                    return (
                        <VariantItem
                            key={`${merchandise.id}${catalogItem.id}`}
                            merchandise={merchandise}
                            catalogItem={catalogItem}
                            totalQuantity={totalQuantity}
                            hasTrackedInventory={hasTrackedInventory}
                            error={error}
                        />
                    );
                })}
            </div>
            {totalQuantity === 0 && <div className="text-sm text-danger">Select at least one variant</div>}
        </div>
    );
});

interface VariantItemProps {
    catalogItem: MerchandiseCatalogItem;
    merchandise: Merchandise;
    totalQuantity: number;
    hasTrackedInventory: boolean;
    error?: string;
}

export const VariantItem = ({
    catalogItem,
    merchandise,
    totalQuantity,
    hasTrackedInventory,
    error,
}: VariantItemProps) => {
    const variant = useCreatePurchaseStore((state) => {
        const wareLineItems = state.getWareLineItems();

        const matchedVariant = wareLineItems.find((lineItem) => lineItem.template?.id === catalogItem.template?.id);
        return { templateId: catalogItem.template?.id ?? "", quantity: matchedVariant?.quantity ?? 0 };
    }, shallow);

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const quantity = variant.quantity ?? 0;
    const minForVariant = getVariantMinQuantity(quantity, totalQuantity);
    const maxForVariant = getMerchandiseVariantMaxSelectableQuantity(catalogItem, hasTrackedInventory);
    const hasAvailability = isVariantPurchasable(catalogItem, hasTrackedInventory);
    const isNoPhysicalStockAvailable = isVariantOutOfStock(catalogItem, hasTrackedInventory);
    const isAvailable = isMerchandiseVariantAvailable(catalogItem, hasTrackedInventory);

    const price = getMerchandiseVariantPrice(catalogItem);
    const itemError = !hasAvailability && quantity > 0;
    const hasError = itemError || !!error;

    const changeQuantity = useDebouncedCallback(
        (newQuantity: number) => {
            if (!variant.templateId) {
                return;
            }

            const cappedQuantity = Math.min(newQuantity, maxForVariant);

            useCreatePurchaseStore
                .getState()
                .actions.updateWareQuantity(activeItemIndex, variant.templateId, Math.max(cappedQuantity, 0));
        },
        100,
        { leading: true, trailing: true },
    );

    return (
        <div
            className={clsx("min-h-12 flex w-full items-center justify-between p-1", {
                "opacity-50": !isAvailable,
            })}
            data-testid="create-purchase-variant-picker-item"
        >
            {catalogItem.photo ? (
                <CatalogItemImage catalogItem={catalogItem} size="small" className="mr-2 shrink-0" />
            ) : (
                <ProductImage product={merchandise} size="small" className="mr-2 shrink-0" />
            )}
            <div className="flex-1">
                <NoTranslate
                    as="div"
                    className={clsx("whitespace-pre-wrap break-all font-bold line-clamp-2", {
                        "text-danger": hasError,
                    })}
                >
                    {catalogItem.name}
                </NoTranslate>
                <div className="text-sm text-gray-darker">
                    <Currency>{price}</Currency>/each
                </div>
            </div>
            <div className="flex items-center gap-3">
                {isNoPhysicalStockAvailable && (
                    <Badge color="danger" className="whitespace-nowrap !rounded-full px-4 !py-1.5 text-sm">
                        Out of Stock
                    </Badge>
                )}
                {hasAvailability && (
                    <QuantitySpinner
                        disabled={!isAvailable}
                        value={quantity}
                        min={minForVariant}
                        max={maxForVariant}
                        minTooltipText="The minimum number of items needed in order to book is"
                        shouldSyncValue={false}
                        onChange={changeQuantity}
                    />
                )}
            </div>
        </div>
    );
};
